<script setup>
import { computed, ref } from 'vue';
import DynamicFormInput from '@/components/widgets/DynamicFormInput.vue';
import { useDisplay } from 'vuetify';

const { smAndDown, xs } = useDisplay();

const props = defineProps({
    fieldModels: {
        type: Object,
        required: false
    },
    align: {},
    label: {},
    rows: {},
    fullWidth: {}, //Muestra el elemento en todo el ancho, con el label arriba y el contenido abajo
    oneColumn: {}, //Muestra el elemento en todo el ancho, pero manteniendo el label al lado izquierdo y el contenido al lado derecho
    labelRow: {}, //Muestra el contenido a una sola columna, pero con el label arriba y el contenido abajo
    twoColumnsMobile: {},
    component: {},
    modelValue: {},
    disabled: {},
    multiple: {},
    required: {},
    errorMessages: {},
    mode: {},
    modelName: {},
    returnObject: {},
    editable: {},
    documentType: {},
    selectedRoles: {},
    show: {
        type: Boolean,
        default: true
    },
    hideLabel: {
        type: Boolean,
        default: false
    },
    tooltipText: {
        type: String,
        default: null
    },
    args: {
        type: Object,
        default: () => ({})
    },
    fullHeight: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur']);

const columns = ref(props.fullWidth || props.oneColumn ? 12 : 6);

const value = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value, props.modelName)
});

// Handle focus events
const handleFocus = (event) => {
    const fieldInfo = {
        fieldName: props.modelName || 'unknown',
        fieldValue: props.modelValue,
        label: props.label,
        component: props.component,
        timestamp: new Date().toISOString(),
        element: event.target.tagName,
        type: event.target.type
    };

    emit('focus', fieldInfo, event);
};

// Handle blur events
const handleBlur = (event) => {
    const fieldInfo = {
        fieldName: props.modelName || 'unknown',
        fieldValue: props.modelValue,
        label: props.label,
        component: props.component,
        timestamp: new Date().toISOString(),
        element: event.target.tagName,
        type: event.target.type
    };

    emit('blur', fieldInfo, event);
};
</script>

<template>
    <v-col
        v-if="show"
        :cols="xs ? (twoColumnsMobile ? 6 : 12) : columns"
        class="py-0 mb-2"
        :style="{ height: fullHeight ? '100%' : 'auto' }"
    >
        <div
            class="input-container"
            :class="{ 'mb-3': smAndDown }"
            :style="{ height: fullHeight ? '100%' : 'auto' }"
        >
            <v-row no-gutters>
                <v-col
                    v-if="
                        (columns == 6 || props.oneColumn) &&
                        !smAndDown &&
                        !props.labelRow
                    "
                    class="py-0 d-flex align-center"
                >
                    <label v-if="!hideLabel">
                        {{ $t(label) }}
                        <v-tooltip v-if="tooltipText" :text="$t(tooltipText)">
                            <template v-slot:activator="{ props }">
                                <v-icon v-bind="props">
                                    mdi-help-circle-outline
                                </v-icon>
                            </template>
                        </v-tooltip>
                    </label>
                </v-col>
                <v-col
                    class="py-0 bg-white position-relative"
                    :class="`form-line-${component}`"
                    :style="{ height: fullHeight ? '100%' : 'auto' }"
                    :data-field-name="modelName"
                >
                    <span v-if="props.fullWidth || props.labelRow || smAndDown">
                        <label v-if="!hideLabel"
                            >{{ $t(label) }}
                            <v-tooltip
                                v-if="tooltipText"
                                :text="$t(tooltipText)"
                            >
                                <template v-slot:activator="{ props }">
                                    <v-icon v-bind="props"
                                        >mdi-help-circle-outline</v-icon
                                    >
                                </template>
                            </v-tooltip>
                        </label>
                    </span>
                    <span v-if="required" class="required-mark">*</span>

                    <DynamicFormInput
                        v-model="value"
                        :component="component"
                        :error-messages="errorMessages"
                        :mode="mode"
                        :align="align"
                        :disabled="disabled"
                        :multiple="multiple"
                        :rows="rows"
                        :return-object="returnObject"
                        :field-models="fieldModels"
                        :editable="editable"
                        :document-type="documentType"
                        :style="{ height: fullHeight ? '100%' : 'auto' }"
                        :args="args"
                        @update:args="(args) => $emit('update:args', args)"
                        @focus="handleFocus"
                        @blur="handleBlur"
                    ></DynamicFormInput>
                </v-col>
            </v-row>
        </div>
        <slot></slot>
    </v-col>
</template>

<style lang="scss" scoped>
.input-container {
    background-image: url(@/assets/input-dots.svg);
    background-repeat: repeat-x;
    background-position: center center;

    label {
        background-color: white;
        padding-right: 5px;
        font-size: 0.875rem;
    }

    :deep(.v-field) {
        min-height: 32px !important;
    }

    :deep(.v-field__input) {
        min-height: 32px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        line-height: 32px !important;
        font-size: 0.875rem !important;
    }

    :deep(.v-text-field) {
        background-color: white;
    }

    :deep(.v-input) {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.v-label) {
        font-size: 0.875rem !important;
        margin-top: -2px !important;
    }
}

.required-mark {
    position: absolute;
    font-size: 1.5rem;
    left: -12px;
    top: 5px;
    background-color: white;
    padding: 0px 1px;
    color: red;
}

.form-line-VSwitch {
    display: flex;
    align-items: center;

    span {
        order: 1;
        margin-left: 20px;
        font-size: 0.875rem;
    }

    div {
        order: 0;
    }
}

.py-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}

.mb-2 {
    margin-bottom: 4px !important;
}
</style>
